import { apiSections }                                                              from 'dmpconnectjsapp-base/constants';
import { getAccessRightsProps }                                                     from 'dmpconnectjsapp-base/rules/accessRights';
import moment                                                                       from 'moment/moment';
import PropTypes                                                                    from 'prop-types';
import * as React                                                                   from 'react';
import { connect }                                                                  from 'react-redux';
import { identifierTypes }                                                          from '../../dmpconnect/constants';
import { extractPersistantDataFromSectionAndDecode, getCardFilesFromPersistedData } from '../../dmpconnect/helpers/persistantData';
import { filterAll, filterAuthor, filterDates, tableSortAuthor, tableSortDates }    from '../../dmpconnect/utils/table';
import Table                                                                        from '../Common/Table/Table';
import AuthorsCell                                                                  from '../DMPViewer/Cells/AuthorsCell';
import ImmunizationNoteDropdownActions                                              from './ImmunizationNoteDropdownActions';

const ImmunizationHistory = ({ structuredBody, immunizationNotes, ins, uniqueId }) => {
  const authorSort = React.useMemo(
    () => (rowA, rowB, columnId) => tableSortAuthor(rowA, rowB, columnId),
    [],
  );
  
  const dateSort = React.useMemo(
    () => (rowA, rowB, columnId) => tableSortDates(rowA, rowB, columnId),
    [],
  );
  
  const filterTypes = React.useMemo(() => ({
    global: filterAll,
    date  : filterDates,
    author: filterAuthor,
  }), []);
  
  const columns = React.useMemo(() => ([
    {
      Header  : 'Date de l\'acte',
      id      : 'date',
      accessor: 's_date',
      Cell    : ({ value }) => moment(value, 'YYYYMMDD').format('DD/MM/YYYY'),
      sortType: dateSort,
      filter  : 'date',
    },
    {
      Header  : 'Nom du vaccin',
      id      : 'name',
      accessor: 'Consumable.s_name',
      filter  : 'text',
    },
    {
      Header  : 'Type de vaccination',
      id      : 'type',
      accessor: 's_typeDescription',
      filter  : 'text',
    },
    {
      Header  : 'Rang de vaccination',
      id      : 'rank',
      accessor: 'i_rank',
      filter  : 'equals',
    },
    {
      Header  : 'Numéro de lot',
      id      : 'lotNumber',
      accessor: 'Consumable.s_lotNumber',
      filter  : 'text',
    },
    {
      Header  : 'Voie d\'administration',
      id      : 'route',
      accessor: 's_immunizationRouteDescription',
      filter  : 'text',
    },
    {
      Header  : 'Site d\'administration',
      id      : 'approachSite',
      accessor: 's_approachSiteDescription',
      filter  : 'text',
    },
    {
      Header  : 'Vaccinateur',
      id      : 'performer',
      accessor: 'Performer',
      filter  : 'author',
      Cell    : ({ value }) => <AuthorsCell authors={[value]}/>,
      sortType: authorSort,
    },
    {
      Header  : 'Vaccin ajouté ou modifié par',
      id      : 'author',
      accessor: 'Author',
      filter  : 'author',
      Cell    : ({ value }) => <AuthorsCell authors={[value]}/>,
      sortType: authorSort,
    },
    {
      Header  : 'Commentaire',
      id      : 'comment',
      accessor: 'Comment.s_text',
      filter  : 'text',
    },
    {
      Header        : 'Action',
      id            : 'action',
      disableFilters: true,
      Cell          : ({ row }) => {
        const
          {
            Author: {
              i_hpInternalIdType: authorIdType
            },
            Performer: {
              i_hpInternalIdType: performerIdType
            } = {}
          } = row.original;
        
        const entryId          = row.original.s_entryId;
        const immunizationNote = immunizationNotes.find(note => note.immunizationNoteEntryId === entryId) || {};
        const { Sections = [] } = structuredBody;
        let nbImmunizations = 0;
        if (Sections.length > 0) {
          nbImmunizations = (Sections[0].Immunizations || []).length;
        }
        return (
          <ImmunizationNoteDropdownActions
            lastOne={nbImmunizations === 1}
            hvUniqueId={uniqueId}
            document={{ ...immunizationNote, ...row.original }}
            canModify={Object.keys(immunizationNote).length > 0}
            canDelete={Object.keys(immunizationNote).length > 0}
            shouldValidate={authorIdType === identifierTypes.insPatient && (!performerIdType || performerIdType === identifierTypes.internalOther)}
            ins={ins}
          />
        );
      }
    }
  ]), [structuredBody]);
  
  const data = React.useMemo(() => {
    const { Sections = [] } = structuredBody;
    if (Sections.length > 0) {
      return Sections[0].Immunizations;
    }
  }, [structuredBody]);
  
  return (
    <Table
      columns={columns}
      data={data}
      filterTypes={filterTypes}
      responsive
      initialState={{ sortBy: [{ desc: true, id: 'date' }] }}
    />
  );
};

ImmunizationHistory.propTypes    = {
  ins              : PropTypes.string.isRequired,
  structuredBody   : PropTypes.object,
  immunizationNotes: PropTypes.array,
  uniqueId: PropTypes.string.isRequired,
};
ImmunizationHistory.defaultProps = {
  structuredBody   : {},
  immunizationNotes: [],
};

function mapStateToProps(state) {
  const { accessRights: { psId } } = getAccessRightsProps(state);
  const {
          dmpconnect: {
            [apiSections.GET_PERSISTANT_DATA]: persistantData,
          },
        }                          = state;
  
  const decodedData = extractPersistantDataFromSectionAndDecode(persistantData);
  
  const userDocs    = getCardFilesFromPersistedData(decodedData, psId);
  const flattenDocs = Object.entries(userDocs || {}).flatMap(([_, docs]) => Object.values(docs).map(doc => doc));
  
  const immunizationNotes = flattenDocs.filter(doc => !!doc.immunizationNoteEntryId);
  return {
    immunizationNotes,
  };
}

export default connect(mapStateToProps)(ImmunizationHistory);
